import { Dispatch, useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import { OrderSideEnum, TimeInForceEnum } from '../../lib/enums';
import { OrderTypeEnum } from '../../lib/enums/order-type.enum';
import lang from '../../lib/language';
import { Order } from '../../lib/models/trading/types';
import { RootState } from '../../lib/store';
import { getCurrentAccount } from '../../lib/store/crm/helpers';
import { getCurrentBuyingPower } from '../../lib/store/selectors';
import { newOrder as serviceNewOrder } from '../../lib/store/trading/index';
import MarketStateCache from '../../lib/store-util/MarketStateCache';
import NBBOPriceCache from '../../lib/store-util/NBBOPriceCache';
import SymbolStatusCache from '../../lib/store-util/SymbolStatusCache';
import TradePriceCache from '../../lib/store-util/TradePriceCache';
import { formatCurrency } from '../../lib/util/DataHelpers';
import { checkIfSymbolIsHalted } from '../../lib/util/HaltSymbolHelper';
import {
  isMarketClosed,
  isMarketOpen,
  isMarketRegularHours,
} from '../../lib/util/MarketDataHelpers';
import { uuidv4 } from '../../lib/util/tools';
import { getOrderTotalValue } from '../../lib/util/TradingValidationsHelper';

import ArrowBack from '../../assets/img/blue_icon-arrow-left.svg';
import { ClickableDiv, HighLowLimitPriceAlert, MaintenanceNotice, NotEnoughBuyingPower } from '../../components';
import ConfirmOrderPopupMobile from '../../components/ConfirmOrderPopupMobile/ConfirmOrderPopupMobile';
import Error from '../../components/Error/Error';
import LimitPriceInput from '../../components/OrderActions/components/LimitPriceInput';
import MarketPriceInfo from '../../components/OrderActions/components/MarketPriceInfo';
import { OrderTypeDropdownOptions } from '../../components/OrderActions/order.constants';
import { getNameByOrderType, getValidationPrice } from '../../components/OrderActions/OrderHelpers';
import useValidateOrder from '../../components/OrderActions/useValidateOrder';
import SharesCalculator from '../../components/SharesCalculator/SharesCalculator';
import { useMaintenanceMode, useTradingButtonsDisabled } from '../../hooks';
import Button from '../../shared_components/Button/Button';
import CommonDropdown from '../../shared_components/CommonDropdown/CommonDropdown';

import styles from './BuyAndSell.module.scss';

type Props = ReduxProps;

const BuyAndSell = (props: Props) => {
  const { positions, account, buyingPow, newOrder, accountStatusData } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const isMaintenance = useMaintenanceMode();

  const [symbol, setSymbol] = useState<string>('');
  const [target, setTarget] = useState<string>('');
  const [originPath, setOriginPath] = useState<string>('');

  useEffect(() => {
    const { stateSymbol, stateTarget, origin } = (location.state as any) || {};
    setSymbol(stateSymbol);
    setTarget(stateTarget);
    setOriginPath(origin);
  }, [location.state]);

  const isBuyOrder = target === 'Buy';
  const symbolPosition = positions.find((el) => el.symbol === symbol);
  const symbolsStatusCache = SymbolStatusCache.use(symbol);
  const { marketState } = MarketStateCache.use();
  const showMarketInfoIcon = isMarketOpen(marketState);
  const marketClosed = isMarketClosed(marketState);
  const isMarketHours = isMarketRegularHours(marketState);
  const { currentPrice: tradePrice = 0 } = TradePriceCache.use(symbol, 'CreateOrder');
  const nbboData = NBBOPriceCache.use(symbol);
  const { askprice, bidprice } = nbboData;
  const [targetPrice, setTargetPrice] = useState<number>(0);
  const [targetPriceError, setTargetPriceError] = useState<string>('');
  const [orderType, setOrderType] = useState<string>(
    isMarketHours ? OrderTypeEnum.Market : OrderTypeEnum.Limit,
  );
  const [quantity, setQuantity] = useState<number>(1);
  const [numberOfSharesError, setNumberOfSharesError] = useState<string>('');
  const [showReviewOrder, setShowReviewOrder] = useState<boolean>(false);
  const [openedEnoughBPModal, setOpenedEnoughBPModal] = useState<boolean>(false);
  const [showSharesCount, setShowSharesCount] = useState<boolean>(false);
  const [visibleHighLowLimitPriceAlert, setVisibleHighLowLimitPriceAlert] = useState<boolean>(false);
  const isMarketOrder = orderType === OrderTypeEnum.Market;
  const isButtonsDisabled = useTradingButtonsDisabled({ symbol, accountStatusData });

  const validationPirce = getValidationPrice(
    marketClosed,
    askprice,
    bidprice,
    tradePrice,
    isBuyOrder,
  );
  const { errorMessage, controlCheck } = useValidateOrder(
    orderType,
    validationPirce ?? 0,
    targetPrice,
    isBuyOrder,
  );

  const checkIfOrderIsDisabled = (option: string) => {
    if (isMarketHours) return false;
    if (option === OrderTypeEnum.Market) return true;
    if (option === OrderTypeEnum.Stop) return true;
    if (option === OrderTypeEnum.Limit) return false;
    return true;
  };

  const orderTypeDropdownOptions = OrderTypeDropdownOptions(isBuyOrder).map((option) => ({
    id: String(option.value),
    label: option.text ?? '',
    icon: option.icon,
    subText: option.subText,
    disabled: checkIfOrderIsDisabled(option.value as string),
  }));

  const totalValue = getOrderTotalValue(orderType, quantity, tradePrice, targetPrice);

  const isBuyingPowerEnough = buyingPow > totalValue;

  useEffect(() => {
    setShowSharesCount(
      !!(
        (target === OrderSideEnum.Sell && symbolPosition && symbolPosition.quantity > 0)
        || (target === OrderSideEnum.Buy && symbolPosition && symbolPosition.quantity < 0)
      ),
    );
  }, [symbolPosition, target]);

  const isValidOrder = useCallback((): boolean => {
    setTargetPriceError(errorMessage);
    const buyingPowerCheck = totalValue < buyingPow;

    if (!showSharesCount && controlCheck && !buyingPowerCheck) {
      setOpenedEnoughBPModal(true);
    }

    if (showSharesCount && symbolPosition) {
      return Math.abs(symbolPosition.quantity) >= quantity && controlCheck;
    }

    return buyingPowerCheck;
  }, [
    buyingPow,
    controlCheck,
    errorMessage,
    quantity,
    showSharesCount,
    symbolPosition,
    totalValue,
  ]);

  const createOrder = () => {
    const orderObject: Order = {};
    if (orderType === OrderTypeEnum.Stop) {
      orderObject.stopPrice = targetPrice;
    }

    orderObject.account = account;
    orderObject.clientOrderId = uuidv4();
    orderObject.ordType = orderType as OrderTypeEnum;
    orderObject.orderQty = quantity;
    orderObject.side = target as OrderSideEnum;
    orderObject.symbol = symbol;

    if (!isMarketOrder) {
      orderObject.price = targetPrice;
    }

    sessionStorage.setItem('showUseDetailsEndModal', 'true');
    newOrder(orderObject);
    setShowReviewOrder(false);
    navigate(
      {
        pathname: originPath,
      },
      { state: { data: orderObject }, replace: true },
    );
  };

  const onSubmit = () => {
    if (orderType === OrderTypeEnum.Limit && tradePrice && askprice && bidprice) {
      if ((isBuyOrder && targetPrice > askprice) || (!isBuyOrder && targetPrice < bidprice)) {
        setVisibleHighLowLimitPriceAlert(
          isBuyOrder ? targetPrice > askprice : targetPrice < bidprice,
        );
        return;
      }
    }
    if (isValidOrder()) {
      setShowReviewOrder(true);
    }
  };

  const calculateMaxQuantity = () => {
    const positionQuantity = symbolPosition?.quantity;
    if (positionQuantity) {
      const isMaximum = (positionQuantity < 0 && isBuyOrder) || (positionQuantity > 0 && !isBuyOrder);
      return isMaximum ? Math.abs(positionQuantity) : undefined;
    }
    return undefined;
  };

  const onReviewOrderButtonClicked = () => {
    if (errorMessage) {
      setTargetPriceError(errorMessage);
    } else {
      onSubmit();
    }
  };

  return (
    <div className={styles.wrapper}>
      <ClickableDiv
        className={styles.backArrow}
        onClick={() => {
          navigate(
            {
              pathname: originPath,
            },
            { replace: true },
          );
        }}
      >
        <img src={ArrowBack} alt="Back" />
      </ClickableDiv>
      <div
        className={styles.headerText}
      >
        {`${symbol} ${isBuyOrder ? OrderSideEnum.Buy : OrderSideEnum.Sell} Order`}
      </div>
      <div className={styles.wrapperOrderContainer}>
        <div className={styles.wrapperOrderTypeDropDown}>
          <div className={styles.orderTypeLabel}>{lang.commonTradingOrderType()}</div>
          <div className={styles.orderTypeDropDown}>
            <CommonDropdown
              options={orderTypeDropdownOptions}
              selectedOption={{ id: orderType, label: getNameByOrderType(orderType) }}
              onSelectOption={option => setOrderType(option.id)}
              title={lang.mobileTradingTicketSelectOrderType()}
            />
          </div>
        </div>
        <div className={styles.wrapperNumberOfShares}>
          <div className={showSharesCount ? undefined : styles.sharesInfo}>
            <div className={styles.numberOfSharesText}>
              {lang.mobileTradingTicketNumberOfShares()}
            </div>
            {showSharesCount && symbolPosition && (
              <div className={styles.numberOfSharesSubText}>
                {isBuyOrder
                  ? lang.webTradingShortedStocks(Math.abs(symbolPosition.quantity))
                  : lang.webTradingAvailableShares(Math.abs(symbolPosition.quantity))}
              </div>
            )}
          </div>
          <div className={styles.wrapperSharesField}>
            <SharesCalculator
              disabled={checkIfSymbolIsHalted(symbolsStatusCache)}
              quantity={quantity}
              setQuantity={setQuantity}
              maxQuantity={calculateMaxQuantity()}
              clearNumberOfSharesError={setNumberOfSharesError}
            />
          </div>
        </div>
        {orderType !== OrderTypeEnum.Market && (
          <div className={styles.wrapperInputPrice}>
            <LimitPriceInput
              disabled={checkIfSymbolIsHalted(symbolsStatusCache)}
              initialPrice={targetPrice}
              orderType={orderType}
              setTargetPrice={(value) => setTargetPrice(value)}
              targetPriceError={targetPriceError}
              setTargetPriceError={(value) => setTargetPriceError(value)}
              errorClassName={styles.errorMessage}
            />
          </div>
        )}
        {numberOfSharesError && numberOfSharesError.trim() !== '' && (
          <div className={styles.errorWrapper}>
            <Error hiddenIcon errorMessage={numberOfSharesError} />
          </div>
        )}
        <div className={styles.wrapperPriceInfo}>
          <MarketPriceInfo
            askPrice={askprice}
            bidPrice={bidprice}
            symbol={symbol}
            tradePrice={tradePrice}
            isMarketOpen={showMarketInfoIcon}
            isSymbolDetailsBigScreen
          />
        </div>
        {!isMarketOrder && (
          <div className={styles.expiryDateContainer}>
            <div className={styles.expiryDateText}>
              {lang.headerMyAccountPendingOrderExpiryDate2()}
            </div>
            <div className={styles.rightAligned}>
              {TimeInForceEnum.EndOfDay}
            </div>
          </div>
        )}
        <div className={styles.borderLine} />
        <div className={styles.totalValueContainer}>
          <div className={styles.text}>{lang.commonTradingOrderValue()}</div>
          <div className={classNames(styles.value, { [styles.redValue]: !isBuyingPowerEnough })}>
            {formatCurrency(totalValue)}
          </div>
        </div>
        {!showSharesCount && (
          <div className={styles.availableBuyingPowerContainer}>
            <div className={styles.text}>{lang.mobileOrderCreateBuyingPower()}</div>
            <div className={styles.value}>{formatCurrency(buyingPow)}</div>
          </div>
        )}
        {openedEnoughBPModal && (
          <NotEnoughBuyingPower closeModal={() => setOpenedEnoughBPModal(false)} />
        )}
        {visibleHighLowLimitPriceAlert && (
          <HighLowLimitPriceAlert
            isBuyOrder={isBuyOrder}
            processModal={() => {
              setVisibleHighLowLimitPriceAlert(false);
              setShowReviewOrder(true);
            }}
            toggleModal={() => setVisibleHighLowLimitPriceAlert(false)}
          />
        )}
        {showReviewOrder && controlCheck && (
          <ConfirmOrderPopupMobile
            symbol={symbol}
            action={target}
            orderType={orderType}
            sharescount={quantity}
            marketPrice={tradePrice}
            targetPrice={targetPrice}
            orderValue={totalValue}
            onCancel={setShowReviewOrder}
            onConfirm={createOrder}
          />
        )}
      </div>
      {isMaintenance ? (
        <div className={styles.submitButton}>
          <MaintenanceNotice />
        </div>
      ) : (
        <Button
          className={styles.submitButton}
          disabled={quantity <= 0 || isButtonsDisabled}
          biggerFontSize
          onClick={onReviewOrderButtonClicked}
        >
          {lang.mobileOrderCreateButtonReviewOrder()}
        </Button>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  positions: state.reporting.openPositions,
  buyingPow: getCurrentBuyingPower(state),
  account: getCurrentAccount(state),
  accountStatusData: state.closeout.accountStatusData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  newOrder: (order: Order) => dispatch(serviceNewOrder({ order })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(BuyAndSell);
